/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';
import { useCallback, useImperativeHandle, useRef } from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import { findUntil } from '../_common/utils';
import { Divider } from '../divider';
import { ChevronDownIconOutline } from '../icons/generated/heroIcons/ChevronDownIconOutline';
import { Typography } from '../typography';
import { AccordionContext, useAccordionContext } from './accordion-context';
import {
  type AccordionItemId,
  type AccordionItemProps,
  type AccordionProps,
  type ClassicItemProps,
  type IsMultiple,
  type IsNotMultiple,
} from './accordion-types';

enum AccordionSelector {
  Item = '.ndl-accordion-item',
  ItemNotDisabled = '.ndl-accordion-item:not(.ndl-accordion-item-disabled)',
}

const getAccordionItem = (
  accordionElement: Element,
  selector: string,
  direction: 'next' | 'prev' = 'next',
): Element | null => {
  const itemParent = accordionElement.closest(AccordionSelector.Item);
  if (!itemParent) {
    return null;
  }

  return findUntil(direction, itemParent, selector);
};

const AccordionComponent = function AccordionComponent<
  T extends AccordionItemId,
  U extends React.ElementType = 'div',
>({
  children,
  as,
  isMultiple,
  onChange,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<U, AccordionProps<T>>) {
  const accordionRef = useRef<U>(null);

  useImperativeHandle(ref, () => accordionRef.current!);

  const Component: React.ElementType = as || 'div';

  // The following function includes code needed to be
  // able to navigate with the arrow keys (not tab).
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const accordionElement = accordionRef.current;
    if (!accordionElement) {
      return;
    }
    const activeElement = document.activeElement as HTMLElement;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      const newFocusedElement = getAccordionItem(
        activeElement,
        AccordionSelector.ItemNotDisabled,
        'next',
      );
      const newHeader = newFocusedElement?.firstElementChild;

      if (newHeader && newHeader instanceof HTMLElement) {
        focusAccordionItem(newHeader as HTMLElement);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      const newFocusedElement = getAccordionItem(
        activeElement,
        AccordionSelector.ItemNotDisabled,
        'prev',
      );
      const newHeader = newFocusedElement?.firstElementChild;

      if (newHeader && newHeader instanceof HTMLElement) {
        focusAccordionItem(newHeader as HTMLElement);
      }
    } else if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      activeElement.click();
    }
  };

  const focusAccordionItem = (accordionHtmlElement: HTMLElement) => {
    const accordionButton = accordionHtmlElement
      .getElementsByClassName('ndl-accordion-item-header-button')
      .item(0);

    if (accordionButton !== null && accordionButton instanceof HTMLElement) {
      accordionButton.focus();
    }
  };

  const classes = classNames('ndl-accordion', className);

  // Destructure and omit keys from DOM spread
  const { expandedItemIds, expandedItemId, ...restAccordionProps } =
    restProps as Record<string, unknown> as {
      expandedItemIds?: T[];
      expandedItemId?: T | null;
      [key: string]: unknown;
    };

  const contextValue = isMultiple
    ? ({
        expandedItemIds,
        isMultiple,
        onChange,
      } as IsMultiple<T>)
    : ({
        expandedItemId,
        isMultiple,
        onChange,
      } as IsNotMultiple<T>);

  return (
    <Component
      className={classes}
      style={style}
      ref={accordionRef}
      onKeyDown={handleKeyDown}
      role="presentation"
      {...restAccordionProps}
      {...htmlAttributes}
    >
      <AccordionContext.Provider value={contextValue}>
        {children}
      </AccordionContext.Provider>
    </Component>
  );
};

const createItemId = (type: string, id: AccordionItemId) =>
  `ndl-accordionitem${type}id-${id}`;

const BaseAccordionItem = <
  T extends AccordionItemId,
  U extends React.ElementType = 'div',
>({
  itemId,
  children,
  title,
  className = '',
  arrowPosition = 'leading',
  isDisabled = false,
  onExpandedChange,
  htmlAttributes,
  style,
  as,
  titleTypographyVariant = 'subheading-medium',
  hasDivider = true,
  trailingContent,
  headingLevel = 2,
}: PolymorphicCommonProps<U, AccordionItemProps<T>>) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const innerContentRef = useRef<HTMLDivElement>(null);
  const itemElementId = createItemId('item', itemId);
  const headerElementId = createItemId('header', itemId);
  const buttonElementId = createItemId('button', itemId);
  const panelElementId = createItemId('panel', itemId);
  const Component: React.ElementType = as || 'div';

  const context = useAccordionContext();
  const { isMultiple } = context;

  const isExpanded = isMultiple
    ? context.expandedItemIds.includes(itemId)
    : context.expandedItemId === itemId;

  const handleOnClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    // Custom callback to call.
    if (onExpandedChange !== undefined) {
      onExpandedChange(!isExpanded);
    }

    if (isMultiple) {
      const { expandedItemIds, onChange }: IsMultiple<T> = context;
      // Multiple expanded.
      if (isExpanded) {
        // Remove from list.
        const newArray: T[] = expandedItemIds.filter(
          (activeId) => activeId !== itemId,
        );
        onChange(newArray);
      } else if (!isExpanded) {
        // Add to list.
        const newArray = [...expandedItemIds];
        newArray.push(itemId);
        onChange(newArray);
      }
    } else {
      const { onChange }: IsNotMultiple<T> = context;
      // Single expanded.
      if (isExpanded) {
        // Set null.
        onChange(null);
      } else if (!isExpanded) {
        // Set to id.
        onChange(itemId);
      }
    }

    // The W3 WAI-ARIA states that focus can only happen on the header not the header button.
    document.activeElement?.parentElement?.focus();
  }, [isExpanded, isMultiple, isDisabled, itemId, onExpandedChange, context]);

  const classes = classNames('ndl-accordion-item', className, {
    'ndl-accordion-item-disabled': isDisabled,
    'ndl-accordion-item-expanded': isExpanded,
  });

  const headerClasses = classNames('ndl-accordion-item-header', {
    'ndl-accordion-item-header-disabled': isDisabled,
  });

  const iconClasses = classNames('ndl-accordion-item-header-icon-wrapper', {
    'ndl-accordion-item-header-icon-wrapper-leading':
      arrowPosition === 'leading',
  });

  const buttonClasses = classNames('ndl-accordion-item-header-button', {
    'ndl-accordion-item-header-button-disabled': isDisabled,
  });

  const titleClasses = classNames('ndl-accordion-item-header-button-title', {
    'ndl-accordion-item-header-button-title': isDisabled,
    'ndl-accordion-item-header-button-title-leading':
      arrowPosition === 'leading',
  });

  const contentClasses = classNames('ndl-accordion-item-content', {
    'ndl-accordion-item-content-expanded': isExpanded,
    'ndl-accordion-item-content-leading': arrowPosition === 'leading',
  });

  return (
    <Component
      className={classes}
      style={style}
      id={itemElementId}
      {...htmlAttributes}
    >
      <div className={headerClasses} id={headerElementId}>
        <Typography
          as={`h${headingLevel}`}
          variant={titleTypographyVariant}
          className="ndl-accordion-item-heading"
        >
          <button
            type="button"
            id={buttonElementId}
            onClick={handleOnClick}
            className={buttonClasses}
            aria-expanded={isExpanded}
            aria-disabled={isDisabled}
            aria-controls={panelElementId}
            disabled={isDisabled}
          >
            <span className={iconClasses}>
              <span className={titleClasses}>{title}</span>

              <ChevronDownIconOutline
                className={classNames('ndl-accordion-item-header-icon', {
                  'ndl-expanded': isExpanded,
                })}
              />
            </span>
          </button>
        </Typography>
        {trailingContent && (
          <Typography as="div" variant="body-medium">
            {trailingContent}
          </Typography>
        )}
      </div>

      <div
        id={panelElementId}
        ref={contentRef}
        className={contentClasses}
        aria-hidden={!isExpanded}
        aria-labelledby={buttonElementId}
        role="region"
      >
        {/* The <div> element has a child <button> element that allows keyboard interaction */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          ref={innerContentRef}
          className="ndl-accordion-item-content-inner"
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Typography
            variant="body-medium"
            className="ndl-accordion-item-content-text"
            as="div"
          >
            {children}
          </Typography>
        </div>
      </div>
      {hasDivider && <Divider />}
    </Component>
  );
};

/**
 * @remarks
 * Where `T` is of the type `string | number | undefined`.
 *
 * `TypographyVariants` is of the type `'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subheading-large' | 'subheading-medium' | 'subheading-small' | 'body-large' | 'body-medium' | 'body-small' | 'code' | 'label'`.
 */
const AccordionItem = <
  T extends AccordionItemId,
  U extends React.ElementType = 'div',
>({
  className,
  ...restProps
}: PolymorphicCommonProps<U, ClassicItemProps<T>>) => {
  const classes = classNames('ndl-accordion-item-classic', className);

  return <BaseAccordionItem {...restProps} className={classes} />;
};
AccordionItem.displayName = 'Accordion.Item';

/**
 * @remarks
 * Where `T` is of the type `string | number | undefined`.
 */
type CleanItemProps<T extends AccordionItemId> = Omit<
  AccordionItemProps<T>,
  'hasDivider'
>;

const CleanItem = <
  T extends AccordionItemId,
  U extends React.ElementType = 'div',
>({
  className,
  ...restProps
}: PolymorphicCommonProps<U, CleanItemProps<T>>) => {
  const classes = classNames('ndl-accordion-item-clean', className);

  return (
    <BaseAccordionItem {...restProps} className={classes} hasDivider={false} />
  );
};

CleanItem.displayName = 'Accordion.CleanItem';

const Accordion = Object.assign(AccordionComponent, {
  CleanItem: CleanItem,
  Item: AccordionItem,
});

export { Accordion };
